import React, { useMemo } from 'react'
import { Button } from '@t-one-internal/design-system'
import { Globe, ArrowDown, ArrowUp, Users } from '@phosphor-icons/react'
import { useRecognitionStore } from '../../../stores/recognitionStore'
import RecognitionCard from './RecognitionCard'
import FeedSidebar from './FeedSidebar'
import RecognitionFeedSkeleton from './RecognitionFeedSkeleton'
import type { RecognitionType, FeedView } from '../../../types/recognition'

// ─── View Tabs (Tier 1): Whose recognitions to show ─────────────────────────
const VIEW_TABS: { id: FeedView; label: string; icon: React.ReactNode }[] = [
  { id: 'company', label: 'Company Feed', icon: <Globe size={14} weight="bold" /> },
  { id: 'received', label: 'Received', icon: <ArrowDown size={14} weight="bold" /> },
  { id: 'given', label: 'Given', icon: <ArrowUp size={14} weight="bold" /> },
  { id: 'my_team', label: 'My Team', icon: <Users size={14} weight="bold" /> },
]

// ─── Type Chips (Tier 2): What kind of recognition ──────────────────────────
const TYPE_CHIPS: { id: RecognitionType | 'all'; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'kudos', label: 'Kudos' },
  { id: 'spot_award', label: 'Spot Awards' },
  { id: 'shoutout', label: 'Shoutouts' },
  { id: 'thankyou', label: 'Thank You' },
]

// ─── Visibility Matrix: which type chips are visible per view ────────────────
const HIDDEN_TYPES_BY_VIEW: Record<FeedView, Set<RecognitionType | 'all'>> = {
  company: new Set([]),
  received: new Set([]),
  given: new Set([]),
  my_team: new Set(['thankyou']),
}

const RecognitionFeed: React.FC = () => {
  const recognitions = useRecognitionStore((s) => s.recognitions)
  const feedLoading = useRecognitionStore((s) => s.feedLoading)
  const feedFilter = useRecognitionStore((s) => s.feedFilter)
  const feedView = useRecognitionStore((s) => s.feedView)
  const setFeedFilter = useRecognitionStore((s) => s.setFeedFilter)
  const setFeedView = useRecognitionStore((s) => s.setFeedView)
  const highlightedId = useRecognitionStore((s) => s.highlightedRecognitionId)

  const visibleTypeChips = useMemo(() => {
    const hidden = HIDDEN_TYPES_BY_VIEW[feedView]
    return TYPE_CHIPS.filter((chip) => !hidden.has(chip.id))
  }, [feedView])

  if (feedLoading) {
    return <RecognitionFeedSkeleton />
  }

  return (
    <div className="flex gap-0">
      {/* Main Feed */}
      <div className="flex-1 min-w-0 p-6">
        {/* Tier 1: View Tabs */}
        <div className="flex gap-1 mb-3 border-b border-border">
          {VIEW_TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setFeedView(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${feedView === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground/30'
                }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tier 2: Type Chips */}
        <div className="flex flex-wrap gap-2 mb-5">
          {visibleTypeChips.map((f) => (
            <Button
              key={f.id}
              variant={feedFilter === f.id ? 'default' : 'outline'}
              size="sm"
              className="rounded-full"
              onClick={() => setFeedFilter(f.id)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Cards — Grid */}
        {recognitions.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {recognitions.map((rec, index) => (
              <RecognitionCard
                key={rec.id ?? index}
                recognition={rec}
                isHighlighted={String(rec.id) === String(highlightedId)}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center py-12 min-h-[300px]">
            <div className="text-center">
              <div className="mb-4 flex justify-center">
                <img
                  src="/assets/EmptyTable2.png"
                  alt="No recognitions"
                  className="w-32 h-32 object-contain opacity-60"
                />
              </div>
              <p className="text-lg font-medium text-foreground mb-2">No recognitions yet</p>
              <p className="text-sm text-muted-foreground">Be the first to recognise a colleague's achievement!</p>
            </div>
          </div>
        )}
      </div>

      {/* Right Sidebar */}
      <FeedSidebar />
    </div>
  )
}

export default RecognitionFeed
